import { fetchApi } from './api';
import type {
    OverviewData, ByEntityData, ForecastData, FxExposureData, CounterpartyData,
    CreditLineEntry, IntercompanyLoan, DataQualityData, CockpitBankAccount,
    CounterpartyGroup, CreateCounterpartyGroupData, UpdateCounterpartyGroupData,
    PlannedCashEvent, CreatePlannedEventData, UpdatePlannedEventData,
    ForecastBreakdownData, ForecastFlowType,
} from '../types/cashCockpit';

function entitiesParam(entities: string[] | 'ALL' | null | undefined): string {
    if (!entities || entities === 'ALL') return 'ALL';
    return entities.join(',');
}

export const cashCockpitApi = {
    overview: (entities?: string[] | 'ALL', asOfDate?: string) =>
        fetchApi<OverviewData>(
            `/cash-cockpit/overview?entities=${entitiesParam(entities)}${asOfDate ? `&asOfDate=${asOfDate}` : ''}`
        ),

    byEntity: (entities?: string[] | 'ALL', months: number = 12, asOfDate?: string) =>
        fetchApi<ByEntityData>(
            `/cash-cockpit/by-entity?entities=${entitiesParam(entities)}&months=${months}${asOfDate ? `&asOfDate=${asOfDate}` : ''}`
        ),

    forecast: (entities?: string[] | 'ALL', weeks: number = 13, asOfDate?: string, currency?: string) =>
        fetchApi<ForecastData>(
            `/cash-cockpit/forecast?entities=${entitiesParam(entities)}&weeks=${weeks}${asOfDate ? `&asOfDate=${asOfDate}` : ''}${currency ? `&currency=${encodeURIComponent(currency)}` : ''}`
        ),

    forecastBreakdown: (
        entities: string[] | 'ALL' | undefined,
        weekStart: string,
        weekEnd: string,
        flowType: ForecastFlowType,
        currency?: string,
    ) =>
        fetchApi<ForecastBreakdownData>(
            `/cash-cockpit/forecast/breakdown?entities=${entitiesParam(entities)}&weekStart=${weekStart}&weekEnd=${weekEnd}&flowType=${flowType}${currency ? `&currency=${encodeURIComponent(currency)}` : ''}`
        ),

    fxExposure: (entities?: string[] | 'ALL', shock: number = 5, asOfDate?: string) =>
        fetchApi<FxExposureData>(
            `/cash-cockpit/fx-exposure?entities=${entitiesParam(entities)}&shock=${shock}${asOfDate ? `&asOfDate=${asOfDate}` : ''}`
        ),

    counterparty: (entities?: string[] | 'ALL', asOfDate?: string) =>
        fetchApi<CounterpartyData>(
            `/cash-cockpit/counterparty?entities=${entitiesParam(entities)}${asOfDate ? `&asOfDate=${asOfDate}` : ''}`
        ),

    creditLines: async (entities?: string[] | 'ALL', asOfDate?: string) => {
        const res = await fetchApi<{ creditLines: CreditLineEntry[] }>(
            `/cash-cockpit/credit-lines?entities=${entitiesParam(entities)}${asOfDate ? `&asOfDate=${asOfDate}` : ''}`
        );
        return res.creditLines ?? [];
    },

    intercompany: async (entities?: string[] | 'ALL', asOfDate?: string) => {
        const res = await fetchApi<{ loans: IntercompanyLoan[] }>(
            `/cash-cockpit/intercompany?entities=${entitiesParam(entities)}${asOfDate ? `&asOfDate=${asOfDate}` : ''}`
        );
        return res.loans ?? [];
    },

    dataQuality: () => fetchApi<DataQualityData>('/cash-cockpit/data-quality'),

    plannedEventsReadOnly: (entities?: string[] | 'ALL', from?: string, to?: string) =>
        fetchApi<{ events: PlannedCashEvent[] }>(
            `/cash-cockpit/planned-events?entities=${entitiesParam(entities)}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`
        ),

    bankAccounts: () => fetchApi<CockpitBankAccount[]>('/cash-cockpit/bank-accounts'),

    // Counterparty groups (admin)
    listGroups: () => fetchApi<CounterpartyGroup[]>('/cash-cockpit/counterparty-groups'),
    createGroup: (data: CreateCounterpartyGroupData) =>
        fetchApi<{ id: number }>('/cash-cockpit/counterparty-groups', {
            method: 'POST', body: JSON.stringify(data),
        }),
    updateGroup: (id: number, data: UpdateCounterpartyGroupData) =>
        fetchApi<{ id: number }>(`/cash-cockpit/counterparty-groups/${id}`, {
            method: 'PUT', body: JSON.stringify(data),
        }),
    deleteGroup: (id: number) =>
        fetchApi<void>(`/cash-cockpit/counterparty-groups/${id}`, { method: 'DELETE' }),
};

export const plannedEventsApi = {
    list: (companyCode?: string, from?: string, to?: string) => {
        const p = new URLSearchParams();
        if (companyCode) p.set('companyCode', companyCode);
        if (from) p.set('from', from);
        if (to) p.set('to', to);
        return fetchApi<PlannedCashEvent[]>(`/planned-events?${p}`);
    },
    create: (data: CreatePlannedEventData) =>
        fetchApi<{ id: number }>('/planned-events', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: UpdatePlannedEventData) =>
        fetchApi<{ id: number }>(`/planned-events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) =>
        fetchApi<void>(`/planned-events/${id}`, { method: 'DELETE' }),
};
